import * as React from "react";
import { Checkbox, Radio } from "antd";
import { BrowserRowProps } from "../types";
import styles from "../NGProviderBrowser.module.scss";

// Custom comparison - only re-render if visual state changes
function arePropsEqual(prev: BrowserRowProps, next: BrowserRowProps): boolean {
  return (
    prev.itemValue === next.itemValue &&
    prev.isSelected === next.isSelected &&
    prev.isFocused === next.isFocused &&
    prev.rowIndex === next.rowIndex &&
    prev.multipleChoice === next.multipleChoice &&
    prev.rowData === next.rowData &&
    prev.gridTemplateColumns === next.gridTemplateColumns
  );
}

const defaultColumnRenderer = () => null;

export const BrowserRow: React.FC<BrowserRowProps> = React.memo(
  ({
    itemValue,
    rowData,
    labels,
    isSelected,
    isFocused,
    multipleChoice,
    rightAlign,
    monoColumns,
    columnRenderers,
    onSelect,
    onFocus,
    rowIndex,
    gridTemplateColumns,
  }) => {
    // Only the row handles clicks - checkbox/radio just reflects state
    const handleRowClick = React.useCallback(() => {
      onSelect(itemValue);
      onFocus(rowIndex);
    }, [onSelect, onFocus, itemValue, rowIndex]);

    return (
      <div
        className={`${styles.row} ${isSelected ? styles.rowSelected : ""} ${isFocused ? styles.rowFocused : ""}`}
        style={{ gridTemplateColumns }}
        data-row-index={rowIndex}
        onClick={handleRowClick}
      >
        {labels.map((label, labelIndex) => {
          const isFirstLabel = labelIndex === 0;
          const isRightAligned = rightAlign.includes(label);
          const isMono = monoColumns.includes(label);

          const columnData = rowData[labelIndex + 1];
          const columnValue = Array.isArray(columnData) ? columnData[0] : columnData;
          const displayText: string = (Array.isArray(columnData) ? columnData[1] : columnData) || "";

          const columnRenderer =
            columnRenderers.find((c) => c.label === label)?.renderer ?? defaultColumnRenderer;

          const cellClassName = `${styles.cell} ${isRightAligned ? styles.cellRight : ""} ${isMono ? styles.cellMono : ""}`;

          if (isFirstLabel) {
            return (
              <div key={label} className={styles.cell}>
                {multipleChoice ? (
                  <Checkbox
                    id={`${itemValue}-checkbox`}
                    className={styles.checkbox}
                    checked={isSelected}
                  >
                    {columnRenderer(displayText, columnValue, rowData)}
                    <span className={`${styles.cellText} ${isMono ? styles.cellMono : ""}`}>{displayText}</span>
                  </Checkbox>
                ) : (
                  <>
                    <Radio id={`${itemValue}-radio`} checked={isSelected} />
                    <label htmlFor={`${itemValue}-radio`} className={styles.radioLabel}>
                      {columnRenderer(displayText, columnValue, rowData)}
                      <span className={isMono ? styles.cellMono : ""}>{displayText}</span>
                    </label>
                  </>
                )}
              </div>
            );
          }

          return (
            <div key={label} className={cellClassName}>
              {isRightAligned ? (
                <>
                  <pre className={styles.preText}>{displayText}</pre>
                  <div className={styles.rightPadding} />
                </>
              ) : isMono ? (
                <span className={styles.monoText}>{displayText}</span>
              ) : (
                displayText
              )}
            </div>
          );
        })}
      </div>
    );
  },
  arePropsEqual,
);

BrowserRow.displayName = "BrowserRow";
